import { Seo } from "../components/Seo";
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { UsersThree, Handshake, Path, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { useContent } from "../context/ContentContext";
import { resolveImg } from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import { ValuesMarquee } from "../components/ValuesMarquee";
import { Pikorua } from "../components/Pikorua";
import { PartnerSection } from "../components/PartnerSection";
import { REGIONS } from "../data/defaults";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] } }),
};

const HighlightCard = ({ icon: Icon, title, body, image, delay }) => (
  <Reveal delay={delay} className="group">
    <div className="relative h-full bg-white border border-navy/10 overflow-hidden transition-colors hover:border-gold">
      <div className="h-48 overflow-hidden">
        {image && (
          <img
            src={resolveImg(image)}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        )}
      </div>
      <div className="p-8">
        <span className="inline-grid place-items-center h-12 w-12 bg-navy text-gold rounded-sm mb-5">
          <Icon size={26} weight="duotone" />
        </span>
        <h3 className="font-heading text-2xl text-navy font-medium">{title}</h3>
        <p className="mt-3 text-navy/70 leading-relaxed text-sm">{body}</p>
      </div>
      <div className="absolute top-0 left-0 h-full w-1 bg-gold scale-y-0 origin-top transition-transform duration-500 group-hover:scale-y-100" />
    </div>
  </Reveal>
);

export default function Home() {
  const { content } = useContent();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  const regionRef = useRef(null);
  const { scrollYProgress: rp } = useScroll({ target: regionRef, offset: ["start end", "end start"] });
  const regionY = useTransform(rp, [0, 1], ["-8%", "12%"]);

  const words = (content.hero_title || "").trim().split(/\s+/).filter(Boolean);
  const mid = Math.ceil(words.length / 2);
  const heroLines = [words.slice(0, mid).join(" "), words.slice(mid).join(" ")].filter(Boolean);

  return (
    <div data-testid="home-page">
      <Seo title="Te Manawa Table Tennis Association — The Heart of the North Island" description="Te Manawa is a Regional Association of Table Tennis New Zealand serving Taranaki, Bay of Plenty, Taupō and the central plateau. Community, participation and pathways for players of all ages." />
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-navy">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          {content.hero_image && <img src={resolveImg(content.hero_image)} alt="Table tennis in a community hall" className="w-full h-full object-cover" />}
        </motion.div>
        <div className="absolute inset-0 bg-navy/70" />
        <div className="absolute inset-0 noise-overlay" />
        <Pikorua className="absolute right-[-6%] top-[8%] w-[420px] h-[420px] text-gold opacity-10" stroke="#F2A900" />

        <motion.div style={{ y: overlayY }} className="relative h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-24">
          <motion.div initial="hidden" animate="show" className="mb-6">
            <div className="overflow-hidden">
              <motion.div custom={0} variants={line}>
                <Overline className="text-gold">Regional Association · Table Tennis New Zealand</Overline>
              </motion.div>
            </div>
          </motion.div>

          <h1 className="font-heading text-white font-medium tracking-tighter leading-[0.92] text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl">
            {heroLines.map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span className="block" custom={i + 1} variants={line} initial="hidden" animate="show">
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="overflow-hidden mt-6">
            <motion.p custom={3.4} variants={line} initial="hidden" animate="show" className="text-gold font-heading text-xl sm:text-2xl md:text-3xl tracking-tight">
              {content.hero_subtitle}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/membership"
              data-testid="hero-join-btn"
              className="group inline-flex items-center gap-2 bg-gold text-navy font-semibold px-8 py-4 rounded-sm transition-transform hover:-translate-y-0.5"
            >
              Join Us <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold px-8 py-4 rounded-sm transition-colors hover:bg-white hover:text-navy"
            >
              Contact Us
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* WELCOME */}
      <section className="relative bg-cream py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-5">
            <Overline className="text-navy/60">Welcome</Overline>
            <h2 className="font-heading text-4xl sm:text-5xl text-navy font-medium tracking-tight leading-[1.05] mt-5">
              {content.welcome_heading}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-6 md:col-start-7">
            <p className="text-lg md:text-xl text-navy/75 leading-relaxed">{content.welcome_body}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {REGIONS.map((r) => (
                <span key={r} className="text-sm font-medium text-navy border border-navy/15 px-4 py-2 rounded-full">{r}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <ValuesMarquee />

      {/* HIGHLIGHTS */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-16">
            <Overline className="text-navy/60">What we stand for</Overline>
            <h2 className="font-heading text-4xl sm:text-5xl text-navy font-medium tracking-tight mt-5">
              Three things at the heart of Te Manawa
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            <HighlightCard icon={UsersThree} title={content.highlight_community_title} body={content.highlight_community_body} image={content.highlight_community_image} delay={0} />
            <HighlightCard icon={Handshake} title={content.highlight_participation_title} body={content.highlight_participation_body} image={content.highlight_participation_image} delay={0.12} />
            <HighlightCard icon={Path} title={content.highlight_pathways_title} body={content.highlight_pathways_body} image={content.highlight_pathways_image} delay={0.24} />
          </div>
        </div>
      </section>

      {/* REGION */}
      <section ref={regionRef} className="relative h-[70vh] min-h-[520px] overflow-hidden flex items-center">
        <motion.div style={{ y: regionY }} className="absolute inset-0 scale-110">
          {content.region_image && <img src={resolveImg(content.region_image)} alt="Central North Island landscape" className="w-full h-full object-cover" />}
        </motion.div>
        <div className="absolute inset-0 bg-navy/75" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <Reveal>
            <Overline className="text-gold">Our region</Overline>
            <h2 className="font-heading text-white text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight mt-5 max-w-3xl leading-[1.02]">
              {content.region_heading}
            </h2>
            <p className="text-white/80 text-lg mt-6 max-w-2xl leading-relaxed">{content.region_body}</p>
          </Reveal>
        </div>
      </section>

      <PartnerSection />

      {/* CTA */}
      <section className="bg-navy-dark relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-28 flex flex-col md:flex-row md:items-end justify-between gap-10">
          <Reveal>
            <h2 className="font-heading text-white text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight max-w-2xl leading-[1.02]">
              Ready to be part of the whānau?
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/membership"
              data-testid="cta-join-btn"
              className="group inline-flex items-center gap-3 bg-gold text-navy font-semibold text-lg px-10 py-5 rounded-sm transition-transform hover:-translate-y-1"
            >
              Get involved <ArrowUpRight size={22} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
